import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import {
  FileSpreadsheet, Loader2, Upload, CheckCircle2, AlertTriangle,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * TimeseriesPreviewCard
 *
 * Two-phase timeseries import UI:
 *   1. Upload Excel → POST /api/admin/import/timeseries/preview
 *   2. Review conflicts (value changes vs. current DB)
 *   3. Tick individual rows OR "Approve all" OR "Reject all"
 *   4. Apply → POST /api/admin/import/timeseries/commit
 *
 * "New" rows (key not yet in DB) are ALWAYS applied — they're not conflicts.
 * "Unchanged" rows are no-ops.
 */
export const TimeseriesPreviewCard = ({ token }) => {
  const headers = { Authorization: `Bearer ${token}` };
  const [file, setFile] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);          // full API response
  const [approvedKeys, setApprovedKeys] = useState(new Set());
  const [committing, setCommitting] = useState(false);
  const [commitResult, setCommitResult] = useState(null);

  const totalConflicts = preview?.totals?.conflicts || 0;

  const handleFilePick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/\.(xlsx|xls|xlsb)$/i.test(f.name)) {
      toast.error('Please pick an Excel file (.xlsx, .xls, .xlsb)');
      return;
    }
    setFile(f);
    setPreview(null);
    setCommitResult(null);
    setApprovedKeys(new Set());
  };

  const handlePreview = async () => {
    if (!file) { toast.error('Pick a file first'); return; }
    setPreviewing(true);
    setCommitResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(
        `${API_URL}/api/admin/import/timeseries/preview`,
        fd,
        { headers: { ...headers, 'Content-Type': 'multipart/form-data' }, timeout: 300000 },
      );
      setPreview(r.data);
      setApprovedKeys(new Set());   // default: reject all conflicts
      const t = r.data.totals;
      toast.success(
        `Preview ready — ${t.new} new, ${t.unchanged} unchanged, ${t.conflicts} conflicts.`
      );
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Preview failed';
      toast.error(`Preview failed: ${msg}`);
    } finally {
      setPreviewing(false);
    }
  };

  const toggleKey = (key) => {
    setApprovedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const approveAllInSheet = (sheet) => {
    const keys = (preview.conflicts_by_sheet?.[sheet] || []).map(c => c.key);
    setApprovedKeys(prev => new Set([...prev, ...keys]));
  };
  const rejectAllInSheet = (sheet) => {
    const keys = new Set((preview.conflicts_by_sheet?.[sheet] || []).map(c => c.key));
    setApprovedKeys(prev => new Set([...prev].filter(k => !keys.has(k))));
  };

  const approveAll = () => {
    const allKeys = Object.values(preview.conflicts_by_sheet || {}).flat().map(c => c.key);
    setApprovedKeys(new Set(allKeys));
  };
  const rejectAll = () => setApprovedKeys(new Set());

  const handleCommit = async (approveEverything = false) => {
    if (!preview?.preview_id) return;
    setCommitting(true);
    try {
      const body = {
        preview_id: preview.preview_id,
        approved_conflict_keys: approveEverything ? [] : Array.from(approvedKeys),
        approve_all_conflicts: approveEverything,
      };
      const r = await axios.post(
        `${API_URL}/api/admin/import/timeseries/commit`,
        body,
        { headers, timeout: 300000 },
      );
      setCommitResult(r.data);
      const a = r.data.applied || {};
      toast.success(
        `Committed — ${a.new || 0} new inserted, ` +
        `${a.conflicts_approved || 0} conflicts overwritten, ` +
        `${a.conflicts_rejected || 0} conflicts kept.`
      );
      setPreview(null);
      setFile(null);
      setApprovedKeys(new Set());
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Commit failed';
      toast.error(`Commit failed: ${msg}`);
    } finally {
      setCommitting(false);
    }
  };

  const fmt = (v) => v == null ? '-' : Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <Card data-testid="ts-preview-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileSpreadsheet className="h-5 w-5" />
          Preview Timeseries Changes
        </CardTitle>
        <CardDescription>
          Upload an Excel to see EXACTLY what would change in <code>balance_timeseries</code> and
          <code> interest_timeseries</code>. Approve or reject each value-conflict individually.
          New rows are inserted unconditionally.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* File pick + preview */}
        <div className="flex items-center gap-3">
          <input
            type="file"
            id="ts-preview-file"
            className="hidden"
            accept=".xlsx,.xls,.xlsb"
            onChange={handleFilePick}
            data-testid="ts-preview-file-input"
          />
          <label htmlFor="ts-preview-file">
            <Button asChild variant="outline">
              <span><Upload className="h-4 w-4 mr-2" />{file ? file.name : 'Choose file'}</span>
            </Button>
          </label>
          <Button
            onClick={handlePreview}
            disabled={!file || previewing}
            data-testid="ts-preview-run-btn"
          >
            {previewing
              ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Analysing…</>
              : 'Analyse changes'}
          </Button>
        </div>

        {/* Preview summary */}
        {preview && (
          <div className="space-y-4" data-testid="ts-preview-summary">
            <div className="grid grid-cols-3 gap-3">
              <div className="p-3 bg-green-50 rounded border border-green-200">
                <p className="text-xs text-green-700">New rows (auto-applied)</p>
                <p className="text-2xl font-bold text-green-800">{preview.totals.new}</p>
              </div>
              <div className="p-3 bg-gray-50 rounded border border-gray-200">
                <p className="text-xs text-gray-600">Unchanged</p>
                <p className="text-2xl font-bold text-gray-800">{preview.totals.unchanged}</p>
              </div>
              <div className="p-3 bg-amber-50 rounded border border-amber-200">
                <p className="text-xs text-amber-800 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  Value conflicts
                </p>
                <p className="text-2xl font-bold text-amber-900">{preview.totals.conflicts}</p>
              </div>
            </div>

            {/* Sheets diagnostic */}
            {preview.sheets_found?.length > 0 && (
              <div className="text-xs text-gray-600">
                <span className="font-medium">Sheets detected:</span>{' '}
                {preview.sheets_found.map(s => (
                  <Badge key={s} variant="secondary" className="mr-1">{s}</Badge>
                ))}
              </div>
            )}

            {/* Per-sheet conflict tables */}
            {totalConflicts > 0 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={approveAll} data-testid="ts-approve-all-btn">
                    Approve all ({totalConflicts})
                  </Button>
                  <Button size="sm" variant="outline" onClick={rejectAll} data-testid="ts-reject-all-btn">
                    Reject all
                  </Button>
                  <span className="text-sm text-gray-600 ml-2">
                    {approvedKeys.size} of {totalConflicts} approved
                  </span>
                </div>

                {Object.entries(preview.conflicts_by_sheet || {}).map(([sheet, conflicts]) => {
                  if (!conflicts || conflicts.length === 0) return null;
                  return (
                    <div key={sheet} className="border rounded-lg overflow-hidden">
                      <div className="flex items-center justify-between px-3 py-2 bg-gray-100 border-b">
                        <p className="font-medium">
                          {sheet} <span className="text-gray-500 text-sm">— {conflicts.length} conflicts</span>
                        </p>
                        <div className="flex gap-2">
                          <Button size="sm" variant="ghost" onClick={() => approveAllInSheet(sheet)}>
                            Approve sheet
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => rejectAllInSheet(sheet)}>
                            Reject sheet
                          </Button>
                        </div>
                      </div>
                      <div className="max-h-72 overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 sticky top-0">
                            <tr>
                              <th className="p-2 text-left w-10"></th>
                              <th className="p-2 text-left">Loan ID</th>
                              <th className="p-2 text-left">Date</th>
                              <th className="p-2 text-right">Old value</th>
                              <th className="p-2 text-right">New value</th>
                              <th className="p-2 text-right">Δ</th>
                            </tr>
                          </thead>
                          <tbody>
                            {conflicts.map(c => {
                              const delta = c.new_value - c.old_value;
                              const approved = approvedKeys.has(c.key);
                              return (
                                <tr
                                  key={c.key}
                                  className={approved ? 'bg-amber-50' : ''}
                                  data-testid={`ts-conflict-row-${c.key}`}
                                >
                                  <td className="p-2">
                                    <Checkbox
                                      checked={approved}
                                      onCheckedChange={() => toggleKey(c.key)}
                                      data-testid={`ts-conflict-cb-${c.key}`}
                                    />
                                  </td>
                                  <td className="p-2 font-mono text-xs">{c.loan_id}</td>
                                  <td className="p-2">{c.date}</td>
                                  <td className="p-2 text-right font-mono">{fmt(c.old_value)}</td>
                                  <td className="p-2 text-right font-mono">{fmt(c.new_value)}</td>
                                  <td className={`p-2 text-right font-mono ${delta >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                                    {delta >= 0 ? '+' : ''}{fmt(delta)}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Commit buttons */}
            <div className="flex items-center gap-3 pt-2 border-t">
              <Button
                onClick={() => handleCommit(false)}
                disabled={committing}
                data-testid="ts-commit-selected-btn"
              >
                {committing
                  ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Committing…</>
                  : `Apply — ${preview.totals.new} new + ${approvedKeys.size} approved conflicts`}
              </Button>
              {totalConflicts > 0 && (
                <Button
                  variant="outline"
                  onClick={() => handleCommit(true)}
                  disabled={committing}
                  data-testid="ts-commit-all-btn"
                >
                  Apply ALL (accept every conflict)
                </Button>
              )}
            </div>
          </div>
        )}

        {commitResult && (
          <div className="p-3 bg-green-50 rounded border border-green-200 flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            <div className="text-sm">
              <p className="font-medium">Committed.</p>
              <p className="text-gray-700">
                {commitResult.applied?.new || 0} new,{' '}
                {commitResult.applied?.conflicts_approved || 0} overwritten,{' '}
                {commitResult.applied?.conflicts_rejected || 0} kept unchanged.
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TimeseriesPreviewCard;
